import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, HeartHandshake, Menu } from "lucide-react";
import { BRAND } from "@/lib/site";

const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}${BRAND.logoIcon}`;

/* ------------------------------------------------------------------ */
/* Variant A — Centered, monogram-style                                */
/* ------------------------------------------------------------------ */
function VariantA() {
  return (
    <div className="border-b border-brand-border bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between">
        <button className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-border text-brand-ink">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex-1 flex flex-col items-center gap-1">
          <img
            src={LOGO_URL}
            alt="Nguvu Ya Mwanamke"
            className="h-14 sm:h-16 w-auto object-contain"
          />
          <div className="text-center leading-none mt-1">
            <div className="font-serif text-[15px] sm:text-[17px] font-semibold text-brand-ink tracking-tight">
              Nguvu Ya Mwanamke
            </div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-brand-forest font-semibold mt-0.5">
              Foundation
            </div>
          </div>
        </div>
        <button className="hidden md:inline-flex btn-primary text-sm px-4 py-2">
          <HeartHandshake className="h-4 w-4" />
          Donate
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant B — Editorial lockup (recommendation)                       */
/* ------------------------------------------------------------------ */
function VariantB() {
  return (
    <div className="border-b border-brand-border bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-24 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Nguvu Ya Mwanamke"
            className="h-14 sm:h-16 w-auto object-contain"
          />
          <div className="leading-tight border-l border-brand-border/70 pl-3">
            <div className="font-serif text-[16px] sm:text-[19px] font-semibold text-brand-ink tracking-tight uppercase" style={{ letterSpacing: "0.02em" }}>
              Nguvu Ya Mwanamke
            </div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-brand-forest font-semibold mt-0.5">
              Foundation · Tanzania
            </div>
          </div>
        </div>
        <button className="hidden md:inline-flex btn-primary text-sm px-4 py-2">
          <HeartHandshake className="h-4 w-4" />
          Donate
        </button>
        <button className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-border text-brand-ink">
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant C — Wordmark hero                                           */
/* ------------------------------------------------------------------ */
function VariantC() {
  return (
    <div className="border-b border-brand-border bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-24 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src={LOGO_URL}
            alt="Nguvu Ya Mwanamke"
            className="h-10 w-auto object-contain"
          />
          <div className="font-serif text-[18px] sm:text-[24px] text-brand-ink tracking-tight" style={{ letterSpacing: "-0.005em" }}>
            <span className="font-semibold">Nguvu Ya Mwanamke</span>{" "}
            <span className="italic text-brand-forest hidden sm:inline">Foundation</span>
          </div>
        </div>
        <button className="hidden md:inline-flex btn-primary text-sm px-4 py-2">
          <HeartHandshake className="h-4 w-4" />
          Donate
        </button>
        <button className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-border text-brand-ink">
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant D — Split premium (gold divider + full name stacked)        */
/* ------------------------------------------------------------------ */
function VariantD() {
  return (
    <div className="border-b border-brand-border bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-24 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img
            src={LOGO_URL}
            alt="Nguvu Ya Mwanamke"
            className="h-14 sm:h-16 w-auto object-contain"
          />
          <div className="h-10 w-px bg-brand-gold" />
          <div className="leading-tight">
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-brand-forest font-semibold">
              Established · Dar es Salaam
            </div>
            <div className="font-serif text-[18px] sm:text-[22px] text-brand-ink tracking-tight italic mt-1">
              Nguvu Ya Mwanamke
            </div>
          </div>
        </div>
        <button className="hidden md:inline-flex btn-primary text-sm px-4 py-2">
          <HeartHandshake className="h-4 w-4" />
          Donate
        </button>
        <button className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-border text-brand-ink">
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Lab page                                                             */
/* ------------------------------------------------------------------ */

const VARIANTS = [
  {
    key: "A",
    title: "Variant A · Centred monogram",
    tag: "Boutique NGO · Symmetrical",
    node: <VariantA />,
  },
  {
    key: "B",
    title: "Variant B · Editorial lockup",
    tag: "Recommended · Curated · Horizontal",
    node: <VariantB />,
  },
  {
    key: "C",
    title: "Variant C · Wordmark hero",
    tag: "Modern · Bold · Confident",
    node: <VariantC />,
  },
  {
    key: "D",
    title: "Variant D · Split premium",
    tag: "Editorial masthead · Established · Refined",
    node: <VariantD />,
  },
];

export default function HeaderLab() {
  return (
    <div className="min-h-screen bg-brand-sand/40 pb-24">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-forest underline-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Back home
        </Link>
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-forest font-semibold">
            Header lab
          </div>
          <h1 className="mt-3 font-serif text-4xl lg:text-5xl text-brand-ink leading-[1.05]">
            Pick a header. <span className="italic text-brand-forest">Then we ship it.</span>
          </h1>
          <p className="mt-4 text-brand-muted text-lg leading-relaxed max-w-2xl">
            Four takes on the header lockup — same brand, different personalities.
            Reply with the letter you want (A · B · C · D) and I&apos;ll apply it
            across the whole site in a single sweep.
          </p>
        </div>

        <div className="mt-14 space-y-10">
          {VARIANTS.map((v) => (
            <div
              key={v.key}
              data-testid={`variant-${v.key}`}
              className="rounded-3xl overflow-hidden ring-1 ring-brand-border bg-white"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border/70 bg-brand-cream/60">
                <div className="flex items-baseline gap-4">
                  <div className="font-serif italic text-brand-forest text-3xl leading-none">
                    {v.key}
                  </div>
                  <div>
                    <div className="font-serif text-lg text-brand-ink">
                      {v.title}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-brand-muted">
                      {v.tag}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6 bg-brand-sand/30">
                <div className="rounded-2xl overflow-hidden ring-1 ring-brand-border">
                  {v.node}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
